import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button as MuiButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Star as StarIcon } from '@mui/icons-material';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { trackWorkerRemoved } from '@/util/vexo';

interface DismissWorkerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, details: string, blockWorker: boolean) => void;
  workerFullName: string;
  workerInfo?: any;
  job?: any;
  /**
   * Called instead of `window.location.reload()` once the dismissal succeeds
   * (GRAVY-10330). See EnterHoursModal for the reasoning — a reload is fine on
   * Job Detail and destroys the Dashboard V2 queue's context.
   *
   * NB there is deliberately no `shiftId` here: dismissal is a JOB-level
   * operation (`JobWorkers::DismissService` updates the `job_workers` row, which
   * has no shift dimension), so a shift id would be a promise the endpoint
   * cannot keep. Dashboard V2 therefore inherits exactly Job Detail's behaviour.
   */
  onCompleted?: () => void;
}

const DismissWorkerModal: React.FC<DismissWorkerModalProps> = ({
  open,
  onClose,
  onConfirm,
  workerFullName,
  workerInfo,
  job,
  onCompleted,
}) => {
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [reason, setReason] = useState<string>('');
  const [details, setDetails] = useState<string>('');
  const [blockWorker, setBlockWorker] = useState<boolean>(false);
  const [selectContainer, setSelectContainer] = useState<HTMLElement | null>(
    null,
  );
  const [showError, setShowError] = useState(false);
  const [showDetailsError, setShowDetailsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Set container to document.body to render outside modal
    setSelectContainer(document.body);
  }, []);

  useEffect(() => {
    // Reset error when reason is selected
    if (reason) {
      setShowError(false);
    }
  }, [reason]);

  useEffect(() => {
    // Reset error when details are entered
    if (details) {
      setShowDetailsError(false);
    }
  }, [details]);

  useEffect(() => {
    // Reset state when modal opens/closes
    if (!open) {
      setStep('select');
      setReason('');
      setDetails('');
      setBlockWorker(false);
      setShowError(false);
      setShowDetailsError(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleNext = () => {
    if (!reason) {
      setShowError(true);
      return;
    }
    setStep('details');
  };

  const handleConfirm = async () => {
    if (isSubmitting) return; // Prevent multiple clicks

    if (!details.trim()) {
      setShowDetailsError(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const workerId = workerInfo?.id;
      const jobId = job?.id;

      if (!workerId) {
        console.error('Worker ID not found');
        setIsSubmitting(false);
        return;
      }

      if (!jobId) {
        console.error('Job ID not found');
        setIsSubmitting(false);
        return;
      }

      await axiosRubyClient.post('/api/admin/job_workers/dismiss', {
        workerId: workerId,
        jobId: jobId,
        dismissType: reason,
        reasonForDismissal: details,
        shouldBlockWorker: blockWorker,
      });

      trackWorkerRemoved({
        job_id: jobId,
        worker_id: workerId,
        was_overtime: null,
      });

      // Call the onConfirm callback and close modal
      onConfirm(reason, details, blockWorker);
      handleClose();
      if (onCompleted) {
        onCompleted();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error dismissing worker:', error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  };

  const handleClose = () => {
    setStep('select');
    setReason('');
    setDetails('');
    setBlockWorker(false);
    setShowError(false);
    setShowDetailsError(false);
    onClose();
  };

  const getInfoMessage = () => {
    if (reason === 'No longer needed') {
      const creditPercentage = '50%';
      return `This will dismiss the worker as ${reason}. The worker will receive credit for ${creditPercentage} of the scheduled shift.`;
    }
    return `This will dismiss the worker as ${reason}.`;
  };

  const shouldShowCreditMessage = () => {
    return reason === 'No longer needed';
  };

  const getWorkerName = () => {
    if (workerInfo?.user?.firstName && workerInfo?.user?.lastName) {
      return `${workerInfo.user.firstName} ${workerInfo.user.lastName}`;
    }
    return workerFullName;
  };

  const getRatingDisplay = () => {
    if (workerInfo?.averageRating && workerInfo?.jobsRatingCount) {
      return `${workerInfo.averageRating.toFixed(1)} (${workerInfo.jobsRatingCount})`;
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 20px 16px 20px',
        }}
      >
        <Typography
          sx={{
            color: '#1C1C1C',
            fontFamily: 'Manrope',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '22.4px',
          }}
        >
          Resolve Timesheet
        </Typography>
        <IconButton
          onClick={handleClose}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleClose();
          }}
          size="small"
          sx={{ touchAction: 'manipulation' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '0 20px 20px 20px' }}>
        {step === 'select' ? (
          <>
            <Typography
              sx={{
                color: '#1C1C1C',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '22.4px',
                mb: 0.5,
              }}
            >
              Dismiss{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {workerFullName}
              </Box>
            </Typography>
            <Typography
              sx={{
                color: '#5B5B5B',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '22.4px',
                mb: 2.5,
              }}
            >
              Select a reason and include supporting details before resolving.
            </Typography>

            <FormControl fullWidth>
              <Select
                displayEmpty
                value={reason}
                onChange={(e) => setReason(String(e.target.value))}
                renderValue={(selected) =>
                  selected ? selected : 'Select reason for dismissal'
                }
                error={showError}
                MenuProps={{
                  container: selectContainer,
                  style: {
                    zIndex: 99999,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      zIndex: 99999,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  height: 44,
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: showError ? '#DC1515' : '#e0e0e0',
                    borderWidth: showError ? '2px' : '1px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: showError ? '#DC1515' : '#45A735',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: showError ? '#DC1515' : '#45A735',
                  },
                }}
              >
                <MenuItem value={''} disabled>
                  Select reason for dismissal
                </MenuItem>
                <MenuItem value={'No longer needed'}>No longer needed</MenuItem>
                <MenuItem value={'Do not meet requirements'}>
                  Do not meet requirements
                </MenuItem>
                <MenuItem value={'Performance issue'}>
                  Performance issue
                </MenuItem>
                <MenuItem value={'Late'}>Late</MenuItem>
              </Select>
            </FormControl>
            {showError && (
              <Typography
                sx={{
                  color: '#DC1515',
                  fontSize: '12px',
                  mt: 0.5,
                  mb: 0,
                }}
              >
                Please make a selection to continue.
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <MuiButton
                  fullWidth
                  onClick={handleClose}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleClose();
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: '#45A735',
                    color: '#45A735',
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    touchAction: 'manipulation',
                  }}
                >
                  Cancel
                </MuiButton>
              </Grid>
              <Grid item xs={6}>
                <MuiButton
                  fullWidth
                  variant="contained"
                  onClick={handleNext}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleNext();
                  }}
                  sx={{
                    bgcolor: '#45A735',
                    '&:hover': { bgcolor: '#45A735' },
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    touchAction: 'manipulation',
                  }}
                >
                  Next
                </MuiButton>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Typography
              sx={{
                color: '#1C1C1C',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '22.4px',
                mb: 0.5,
              }}
            >
              Dismiss{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {workerFullName}
              </Box>
            </Typography>
            <Typography
              sx={{
                color: '#5B5B5B',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '22.4px',
                mb: 2.5,
              }}
            >
              Select a reason and include supporting details before resolving.
            </Typography>

            {/* Reason Dropdown (editable) */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={reason}
                onChange={(e) => setReason(String(e.target.value))}
                MenuProps={{
                  container: selectContainer,
                  style: {
                    zIndex: 99999,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      zIndex: 99999,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  height: 44,
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#45A735',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#45A735',
                  },
                }}
              >
                <MenuItem value={'No longer needed'}>No longer needed</MenuItem>
                <MenuItem value={'Do not meet requirements'}>
                  Do not meet requirements
                </MenuItem>
                <MenuItem value={'Performance issue'}>
                  Performance issue
                </MenuItem>
                <MenuItem value={'Late'}>Late</MenuItem>
              </Select>
            </FormControl>

            {/* Details Text Area */}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Reason for dismissal (required)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              error={showDetailsError}
              helperText={
                showDetailsError ? 'Please provide details for dismissal' : ''
              }
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />

            {/* Information Box - Only show if reason is "No longer needed" */}
            {shouldShowCreditMessage() && (
              <Box
                sx={{
                  bgcolor: '#FFF4ED',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    color: '#1C1C1C',
                    fontFamily: 'Manrope',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '20px',
                  }}
                >
                  {getInfoMessage()}
                </Typography>
              </Box>
            )}

            {/* Block Worker Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={blockWorker}
                  onChange={(e) => setBlockWorker(e.target.checked)}
                  sx={{
                    color: '#45A735',
                    '&.Mui-checked': {
                      color: '#45A735',
                    },
                  }}
                />
              }
              label="Would you like to block this worker from future jobs?"
              sx={{ mb: 2 }}
            />

            {/* Worker Information */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                padding: '12px',
                borderRadius: '8px',
                bgcolor: '#FAFAFA',
              }}
            >
              <Avatar
                src={workerInfo?.avatarUrl}
                sx={{ width: 48, height: 48 }}
              >
                {getWorkerName()
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()}
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    color: '#1C1C1C',
                    fontFamily: 'Manrope',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '20px',
                    mb: 0.5,
                  }}
                >
                  Worker Name
                </Typography>
                <Typography
                  sx={{
                    color: '#1C1C1C',
                    fontFamily: 'Manrope',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '20px',
                    mb: 0.5,
                  }}
                >
                  {getWorkerName()}
                </Typography>
                {getRatingDisplay() && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <StarIcon
                      sx={{
                        color: '#FFA500',
                        fontSize: '16px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#666666',
                        fontFamily: 'Manrope',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {getRatingDisplay()}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MuiButton
                  fullWidth
                  onClick={handleClose}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleClose();
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: '#45A735',
                    color: '#45A735',
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    touchAction: 'manipulation',
                  }}
                >
                  Cancel
                </MuiButton>
              </Grid>
              <Grid item xs={6}>
                <MuiButton
                  fullWidth
                  variant="contained"
                  onClick={handleConfirm}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    if (!isSubmitting && details.trim()) {
                      handleConfirm();
                    }
                  }}
                  disabled={!details.trim() || isSubmitting}
                  sx={{
                    bgcolor:
                      details.trim() && !isSubmitting ? '#45A735' : '#9E9E9E',
                    '&:hover': {
                      bgcolor:
                        details.trim() && !isSubmitting ? '#45A735' : '#9E9E9E',
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    touchAction: 'manipulation',
                    '&:disabled': {
                      background: '#9E9E9E',
                      color: '#FFF',
                    },
                  }}
                >
                  Confirm
                </MuiButton>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DismissWorkerModal;
